import { isManager, isStaff } from "../roles";
import { CopyPlus, ExternalLink, Eye, Inbox, PackageOpen, Plus } from "lucide-react";
import { Link } from "wouter";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import Deliverables, { DeliverableFrame } from "../components/Deliverables";
import Modal from "../components/Modal";
import StatusBadge from "../components/StatusBadge";
import { PRODUCTION_STAGES, type ProductionStage, type RequestStatus, type ServiceRequest, type SessionUser } from "../types";
import DueCell from "../components/DueCell";
import StageTracker, { StagePill } from "../components/StageTracker";
import RequestTimeline from "../components/RequestTimeline";
import { ageLabel, dueUrgency, isStale } from "../urgency";
import { formatCredits, formatDate, sourceLabelKey } from "../utils";
import { useServiceText } from "../service-text";
import { useT } from "../i18n";
import ShareWork from "../components/ShareWork";

/**
 * The status filters, as translation KEYS rather than words.
 *
 * A module-level data array is invisible to a JSX-shaped leak check, which is
 * exactly how these five stayed English through a translation pass — the guard
 * test now reads object literals too.
 */
const filters: Array<{ id: "all" | RequestStatus; labelKey: string }> = [
  { id: "all", labelKey: "status.all" },
  { id: "pending", labelKey: "status.pending" },
  { id: "approved", labelKey: "status.approved" },
  { id: "in_progress", labelKey: "status.in_progress" },
  { id: "completed", labelKey: "status.completed" },
];

export default function RequestsPage({
  requests,
  user,
  available,
  approvalThreshold,
  onNewRequest,
  onChanged,
}: {
  requests: ServiceRequest[];
  user: SessionUser;
  available: number;
  approvalThreshold: number | null;
  onNewRequest: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const [filter, setFilter] = useState<"all" | RequestStatus>("all");
  const [selected, setSelected] = useState<ServiceRequest | null>(null);
  const visible = useMemo(() => filter === "all" ? requests : requests.filter((request) => request.status === filter), [filter, requests]);

  return (
    <div className="page-stack">
      <section className="requests-summary">
        <div>
          <span className="section-eyebrow">{t("requests.eyebrow2")}</span>
          <h2>{t(isStaff(user.role) ? "requests.staffHeading" : "requests.workflowSub")}</h2>
          <p>{t(isStaff(user.role) ? "requests.staffSub" : "requests.clientSub")}</p>
        </div>
        <button className="button button-primary" onClick={onNewRequest}><Plus size={18} /> {t("requests.new")}</button>
      </section>

      <section className="table-toolbar requests-toolbar">
        <div className="segmented-control" aria-label={t("requests.statusFilters")}>
          {filters.map((item) => {
            const count = item.id === "all" ? requests.length : requests.filter((request) => request.status === item.id).length;
            return <button key={item.id} className={filter === item.id ? "active" : ""} onClick={() => setFilter(item.id)}>{t(item.labelKey)}<span>{count}</span></button>;
          })}
        </div>
        {isStaff(user.role) && <div className="available-note"><strong>{formatCredits(available)}</strong> credits available for approval</div>}
      </section>

      <section className="panel requests-panel">
        {visible.length ? (
          <div className="responsive-table-wrap">
            <table className="data-table requests-table">
              <thead><tr><th>{t("table.request")}</th><th>{t("table.requestedBy")}</th><th>{t("table.source")}</th><th>{t("table.credits")}</th><th>{t("table.due")}</th><th>{t("table.status")}</th><th><span className="sr-only">{t("table.view")}</span></th></tr></thead>
              <tbody>
                {visible.map((request) => (
                  <tr key={request.id}>
                    <td data-label={t("col.request")} className="request-cell">
                      <div className="request-thumb">
                        <DeliverableFrame
                          attachment={request.coverUrl ? { id: "", requestId: request.id, kind: "deliverable", filename: request.title, mime: "image/*", bytes: 0, isCover: true, createdAt: "", uploadedByName: null, version: null, url: request.coverUrl } : null}
                          ratio="1 / 1"
                        />
                      </div>
                      <div>
                        <strong>{request.title}</strong>
                        <span>
                          {st.name(request.service_id, request.service_name)}
                          {isStale(request) && <em className="waiting-flag" title="Waiting on Prismic to approve">Waiting {ageLabel(request.created_at)}</em>}
                          <StagePill stage={request.stage} />
                        </span>
                      </div>
                    </td>
                    <td data-label="Requested by">{request.created_by_name}</td>
                    <td data-label="Source">{t(sourceLabelKey(request.source))}</td>
                    <td data-label={t("col.credits")}><strong>{formatCredits(request.final_credits ?? request.estimated_credits)}</strong></td>
                    <td data-label={t("col.due")}><DueCell request={request} /></td>
                    <td data-label={t("col.status")}><StatusBadge status={request.status} /></td>
                    <td data-label="View">
                      <div className="row-actions">
                        {/* Delivered work gets a way straight to the hand-off. The
                            detail modal is for managing a request; this is for
                            looking at the thing that was made. */}
                        {request.status === "completed" && (
                          <Link href={`/delivery/${request.id}`} className="icon-button table-icon" title={t("requests.openDelivery")} aria-label={t("requests.openDeliveryFor", { title: request.title })}>
                            <PackageOpen size={18} />
                          </Link>
                        )}
                        <button className="icon-button table-icon" onClick={() => setSelected(request)} title={t("requests.viewRequest")} aria-label={t("aria.viewRequest", { name: request.title })}><Eye size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state"><Inbox size={30} /><h4>{t("requests.emptyTitle")}</h4><p>{t("requests.emptyBody")}</p><button className="button button-secondary" onClick={onNewRequest}>{t("requests.createRequest")}</button></div>
        )}
      </section>

      <RequestDetailModal
        request={selected}
        user={user}
        available={available}
        approvalThreshold={approvalThreshold}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); onChanged(); }}
      />
    </div>
  );
}

function RequestDetailModal({
  request,
  user,
  available,
  approvalThreshold,
  onClose,
  onChanged,
}: {
  request: ServiceRequest | null;
  user: SessionUser;
  available: number;
  approvalThreshold: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const [status, setStatus] = useState<RequestStatus>("approved");
  const [finalCredits, setFinalCredits] = useState(0);
  const [note, setNote] = useState("");
  const [promisedDate, setPromisedDate] = useState("");
  const [stage, setStage] = useState<ProductionStage | "">("");
  const [saving, setSaving] = useState(false);

  // Tell an account manager BEFORE they click, not after. The server 403s either
  // way, but that arrives as a 4-second toast while the modal stays open looking
  // ready — so the block is transient and the confusing state is permanent.
  const spendsCredits = status === "approved" || status === "in_progress" || status === "completed";
  // approvalThreshold === null means the gate is off entirely — never warn. Comparing
  // against null would coerce to `finalCredits > 0` and warn on every single request.
  const needsManager = approvalThreshold !== null && isStaff(user.role) && !isManager(user.role)
    && spendsCredits && finalCredits > approvalThreshold;

  function initialize(next: ServiceRequest | null) {
    if (!next) return;
    setFinalCredits(next.final_credits ?? next.estimated_credits);
    setStatus(next.status === "pending" ? "approved" : next.status);
    setPromisedDate(next.promised_date ?? "");
    setStage(next.stage ?? "");
    setNote("");
  }

  const open = Boolean(request);
  const requestKey = request?.id;
  useEffect(() => initialize(request), [requestKey]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!request) return;
    setSaving(true);
    try {
      await dashboardApi.updateRequest(request.id, { status, finalCredits, note, promisedDate, stage });
      toast.success(t("toast.requestMoved", { status: t(`status.${status}`) }));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.updateRequest"));
    } finally {
      setSaving(false);
    }
  }

  if (!request) return null;
  return (
    <Modal open={open} title={t("requests.details")} onClose={onClose} wide>
      <div className="detail-layout">
        <div className="detail-main">
          <div className="detail-heading"><div><span>{st.category(request.service_category)}</span><h3>{request.title}</h3></div><StatusBadge status={request.status} /></div>
          <StageTracker stage={request.stage} />
          <p className="detail-brief">{request.brief}</p>
          <dl className="detail-grid">
            <div><dt>{t("table.service")}</dt><dd>{st.name(request.service_id, request.service_name)}</dd></div>
            <div><dt>{t("table.quantity")}</dt><dd>{request.quantity}</dd></div>
            <div><dt>{t("table.language")}</dt><dd>{request.language}</dd></div>
            <div><dt>{t("requests.platformChannel")}</dt><dd dir="auto">{request.channel || t("requests.notSpecified")}</dd></div>
            <div><dt>{t("requests.preferredDue")}</dt><dd><DueCell request={request} /></dd></div>
            <div>
              <dt>{t("requests.promisedBy")}</dt>
              <dd>{request.promised_date ? formatDate(request.promised_date) : t("requests.notCommitted")}</dd>
            </div>
            <div><dt>{t("table.source")}</dt><dd>{t(sourceLabelKey(request.source))}</dd></div>
            <div><dt>{t("requests.submittedBy")}</dt><dd>{request.created_by_name}</dd></div>
            <div><dt>{t("requests.actionedBy")}</dt><dd>{request.assigned_to_name || t("requests.notActioned")}</dd></div>
            <div><dt>{t("requests.created")}</dt><dd>{formatDate(request.created_at, true)}</dd></div>
          </dl>

        {/* The work itself, directly under the facts about it. */}
        <Deliverables requestId={request.id} user={user} canDeliver={isStaff(user.role)} requestCompleted={request.status === "completed"} />
          {/* Only on delivered work — sharing a request nobody has made yet sends
              a stakeholder to an empty page, which is worse than no button. */}
          <ShareWork requestId={request.id} delivered={request.status === "completed"} />
          {request.status === "completed" && (
            <Link className="button button-primary handoff-link" href={`/delivery/${request.id}`}><PackageOpen size={16} /> {t("requests.openDelivery")}</Link>
          )}
          {request.reference_url && <a className="reference-link" href={request.reference_url} target="_blank" rel="noreferrer"><ExternalLink size={16} /> {t("requests.openReference")}</a>}

          {/* Same thing again. Retainer work repeats — a monthly reel, a weekly set
              of statics — and re-typing the brief every time is the friction that
              makes people phone the account manager instead of using the portal.
              Closes the modal on the way so the reader is not left with a stale
              dialogue over the form they just opened. */}
          <Link className="button button-secondary reorder-link" href={`/request/new?from=${request.id}`} onClick={onClose}>
            <CopyPlus size={16} /> {t("requests.requestAgain")}
          </Link>
        </div>

        <div className="detail-side">
        <aside className="request-credit-summary">
          {/* Once work is billed, the FINAL figure is the truth. Showing the
              estimate on a completed request had the list saying 28 credits and
              this panel saying 8.5 for the same job — the kind of disagreement a
              client notices in a portal that is fundamentally about money. */}
          <span>{t(request.final_credits !== null ? "requests.deducted" : "requests.estimatedDeduction")}</span>
          <strong>{formatCredits(request.final_credits ?? request.estimated_credits)} credits</strong>
          <small>{formatCredits(available)} credits currently available</small>
        </aside>

        {/* Answers "where is my thing", which is the question this dashboard
            was bought to stop people asking over email. */}
        <section className="request-history">
          <h4>{t("requests.history")}</h4>
          <RequestTimeline requestId={request.id} />
        </section>
        </div>
      </div>

      {isStaff(user.role) && request.status !== "completed" && request.status !== "rejected" && request.status !== "canceled" && (
        <form className="request-review-form" onSubmit={save}>
          <div className="review-heading"><span>{t("requestReview.title")}</span><p>{t("requestReview.intro")}</p></div>
          {needsManager && (
            <div
              role="status"
              style={{
                margin: "0 0 14px", padding: "11px 14px", borderRadius: 8,
                background: "#fdf1f1", border: "1px solid #e7c3c3", color: "#7a2222",
                fontSize: 14, lineHeight: 1.45,
              }}
            >
              <strong>A Prismic manager must approve this.</strong>{" "}
              {finalCredits} credits is above the {approvalThreshold}-credit limit for account managers.
              You can still reject or cancel it.
            </div>
          )}
          <div className="form-grid form-grid-three">
            <div className="form-field">
              <label htmlFor="review-status">{t("review.newStatus")}</label>
              <select id="review-status" value={status} onChange={(event) => setStatus(event.target.value as RequestStatus)}>
                <option value="approved">{t("status.approved")}</option>
                <option value="in_progress">{t("status.in_progress")}</option>
                <option value="completed">{t("status.completed")}</option>
                <option value="rejected">{t("status.rejected")}</option>
                <option value="canceled">{t("status.canceled")}</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="review-credits">{t("review.finalCredits")}</label>
              <input id="review-credits" type="number" min="0" step="0.1" value={finalCredits} onChange={(event) => setFinalCredits(Number(event.target.value))} />
            </div>
            <div className="form-field">
              <label htmlFor="review-promised">{t("review.promisedToClient")}</label>
              {/* Separate from the date the client ASKED for. This is the one we
                  commit to, and the one they are shown. */}
              <input id="review-promised" type="date" value={promisedDate} onChange={(event) => setPromisedDate(event.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="review-stage">{t("review.productionStage")}</label>
              <select id="review-stage" value={stage} onChange={(event) => setStage(event.target.value as ProductionStage | "")}>
                <option value="">{t("common.notSet")}</option>
                {PRODUCTION_STAGES.filter((step) => step !== "delivered").map((step) => (
                  <option key={step} value={step}>{t(`stage.${step}`)}</option>
                ))}
              </select>
            </div>
            <div className="form-field review-note-field">
              <label htmlFor="review-note">{t("review.noteLabel")}</label>
              <input id="review-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder={t("review.note")} />
            </div>
          </div>
          <div className="form-actions"><button className="button button-primary" disabled={saving || needsManager}>{saving ? "Saving…" : needsManager ? t("requests.managerApproval") : t("requests.updateRequest")}</button></div>
        </form>
      )}
    </Modal>
  );
}

